import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type MappingPayload, type MappingRow, type MappingGate, type MappingStatus } from '../api/client';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

// Symbol → Device → Part mapping review checkpoint (§5.5). The AI proposes a
// device type per symbol (memory-first, taxonomy fallback); the estimator
// confirms/overrides. Symbols still needing review are flagged red and BLOCK BOM
// generation until resolved. Confirmed choices are remembered across projects.
export function MappingReview() {
  const { t } = useLang();
  const { id = '' } = useParams();
  const [data, setData] = useState<MappingPayload | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setData(await api.getMappings(id)); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Failed to load mappings', fil: 'Hindi na-load ang mga mapping' })); }
  }
  useEffect(() => { void load(); }, [id]);

  function patch(symbolId: string, change: Partial<MappingRow>, gate?: MappingGate) {
    setData((d) => d && {
      ...d,
      mappings: d.mappings.map((m) => m.symbol_id === symbolId ? { ...m, ...change } : m),
      gate: gate ?? d.gate,
    });
  }

  async function setDevice(m: MappingRow, device_key: string) {
    const dev = data?.devices.find((x) => x.key === device_key);
    patch(m.symbol_id, { device_key, device_type: dev?.label ?? null, assembly_hint: dev?.assemblyHint ?? null, status: 'confirmed', source: 'manual' });
    try { const r = await api.updateMapping(id, m.symbol_id, { device_key }); patch(m.symbol_id, {}, r.gate); }
    catch { void load(); }
  }

  async function setStatus(m: MappingRow, status: MappingStatus) {
    patch(m.symbol_id, { status });
    try { const r = await api.updateMapping(id, m.symbol_id, { status }); patch(m.symbol_id, {}, r.gate); }
    catch { void load(); }
  }

  async function setOfci(m: MappingRow, ofci: boolean) {
    patch(m.symbol_id, { ofci: ofci ? 1 : 0 });
    try { await api.updateMapping(id, m.symbol_id, { ofci }); }
    catch { void load(); }
  }

  async function repropose() {
    setBusy(true);
    try { await api.proposeMappings(id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Re-propose failed', fil: 'Nabigo ang re-propose' })); }
    finally { setBusy(false); }
  }

  async function confirmAll() {
    setBusy(true); setError('');
    try {
      const r = await api.confirmAllMappings(id);
      await load();
      if (r.remaining_unmapped > 0) {
        setError(t({
          en: `Confirmed ${r.confirmed} AI-suggested mapping(s). ${r.remaining_unmapped} symbol(s) still have no device — assign or exclude those below.`,
          fil: `Nakumpirma ang ${r.confirmed} AI-suggested mapping(s). ${r.remaining_unmapped} symbol(s) ay wala pang device — i-assign o i-exclude ang mga iyon sa ibaba.`,
        }));
      }
    } catch (e) { setError(e instanceof Error ? e.message : t({ en: 'Confirm-all failed', fil: 'Nabigo ang confirm-all' })); }
    finally { setBusy(false); }
  }

  const gate = data?.gate;
  const rows = data?.mappings ?? [];

  return (
    <div>
      <Link to={`/projects/${id}`} className="muted" style={{ fontSize: 13 }}>← {t({ en: 'Scope Analysis', fil: 'Scope Analysis' })}</Link>
      <h1 style={{ margin: '4px 0 0' }}>{t({ en: 'Symbol → Device → Part mapping', fil: 'Symbol → Device → Part mapping' })}</h1>
      <p className="muted" style={{ marginTop: 4 }}>
        {t({
          en: 'The AI proposes a device type for each legend symbol. Confirm or override every row — this is a reliability checkpoint. ',
          fil: 'Nagmumungkahi ang AI ng device type para sa bawat legend symbol. Kumpirmahin o i-override ang bawat row — isa itong reliability checkpoint. ',
        })}
        <strong>{t({ en: 'It maps types; it never counts.', fil: 'Tina-type lang nito; hindi ito nagbibilang.' })}</strong>
      </p>

      {/* BOM gate */}
      {gate && (
        <div className={`banner ${gate.blocks_bom ? 'banner-danger' : ''}`} style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            {gate.blocks_bom
              ? <><strong>{t({ en: `${gate.needs_review} symbol${gate.needs_review === 1 ? '' : 's'} need review.`, fil: `${gate.needs_review} symbol ang kailangang i-review.` })}</strong> {t({ en: 'BOM generation is blocked until every symbol is resolved.', fil: 'Naka-block ang BOM generation hangga\'t hindi pa naa-resolve ang bawat symbol.' })}</>
              : <><strong>{t({ en: `All ${gate.total} symbols resolved.`, fil: `Lahat ng ${gate.total} symbol ay na-resolve na.` })}</strong> {t({ en: 'Ready to generate the Count Sheet and BOM.', fil: 'Handa nang i-generate ang Count Sheet at BOM.' })}</>}
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {t({
                en: `${gate.confirmed} confirmed · ${gate.proposed} proposed (unconfirmed) · ${gate.needs_review} needs review · ${gate.excluded} excluded`,
                fil: `${gate.confirmed} nakumpirma · ${gate.proposed} iminungkahi (hindi pa kumpirmado) · ${gate.needs_review} kailangang i-review · ${gate.excluded} hindi kasama`,
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(gate.proposed + gate.needs_review) > 0 && (
              <button className="btn btn-primary btn-sm" onClick={confirmAll} disabled={busy}
                title={t({ en: "Accept the AI's device suggestion for every row that has one. Rows with no suggested device still need manual assignment.", fil: 'Tanggapin ang device suggestion ng AI para sa bawat row na mayroon nito. Ang mga row na walang iminungkahing device ay kailangan pa ring i-assign nang manu-mano.' })}>
                {busy ? t({ en: 'Confirming…', fil: 'Kinukumpirma…' }) : t({ en: 'Confirm all AI suggestions', fil: 'Kumpirmahin lahat ng AI suggestions' })}
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={repropose} disabled={busy}>{busy ? t({ en: 'Re-proposing…', fil: 'Nire-propose…' }) : t({ en: 'Re-propose', fil: 'I-re-propose' })}</button>
          </div>
        </div>
      )}
      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        {!rows.length && <p className="muted">{t({ en: 'No symbols yet. Upload and analyze a plan set first.', fil: 'Wala pang symbol. Mag-upload at mag-analyze muna ng plan set.' })}</p>}
        {rows.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>{t({ en: 'Tag', fil: 'Tag' })}</th><th>{t({ en: 'Legend description', fil: 'Legend description' })}</th><th>{t({ en: 'System', fil: 'System' })}</th>
                <th>{t({ en: 'Proposed device type', fil: 'Iminungkahing device type' })}</th><th>{t({ en: 'Suggested part family', fil: 'Iminungkahing part family' })}</th>
                <th>{t({ en: 'Conf.', fil: 'Conf.' })}</th><th title={t({ en: 'Owner-furnished, contractor-installed: we install it but the owner buys the device', fil: 'Owner-furnished, contractor-installed: kami ang nag-i-install pero ang owner ang bumibili ng device' })}>OFCI</th><th>{t(G.status)}</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.symbol_id} style={rowStyle(m.status)}>
                  <td><strong>{m.symbol_tag ?? '—'}</strong></td>
                  <td>{m.legend_description ?? '—'}</td>
                  <td className="muted">{m.system_name ?? '—'}</td>
                  <td>
                    <select value={m.device_key ?? ''} onChange={(e) => setDevice(m, e.target.value)} style={{ minWidth: 200 }}>
                      <option value="" disabled>{t({ en: '— select device —', fil: '— pumili ng device —' })}</option>
                      {(data?.devices ?? []).map((d) => (
                        <option key={d.key} value={d.key}>{d.label} · {d.system}</option>
                      ))}
                    </select>
                  </td>
                  <td className="muted" style={{ fontSize: 12, maxWidth: 280 }}>{m.assembly_hint ?? '—'}</td>
                  <td>{m.confidence ? `${Math.round(m.confidence * 100)}%` : '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={!!m.ofci} title={t({ en: 'Owner-furnished, contractor-installed (we install; owner buys the device)', fil: 'Owner-furnished, contractor-installed (kami ang nag-i-install; owner ang bumibili ng device)' })}
                      onChange={(e) => setOfci(m, e.target.checked)} />
                  </td>
                  <td><span className={`pill pill-${m.status}`}>{statusLabel(t, m.status)}</span>
                    {m.source === 'memory' && <div className="muted" style={{ fontSize: 11 }}>{t({ en: 'from memory', fil: 'mula sa memory' })}</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {m.status !== 'confirmed' && m.device_key && (
                      <button className="btn btn-ghost btn-sm" onClick={() => setStatus(m, 'confirmed')}>{t(G.confirm)}</button>
                    )}
                    {m.status !== 'excluded'
                      ? <button className="btn btn-ghost btn-sm" onClick={() => setStatus(m, 'excluded')} title={t({ en: 'Not in this scope', fil: 'Wala sa scope na ito' })}>{t({ en: 'Exclude', fil: 'I-exclude' })}</button>
                      : <button className="btn btn-ghost btn-sm" onClick={() => setStatus(m, 'needs_review')}>{t({ en: 'Restore', fil: 'Ibalik' })}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Human-readable status label. `t` is passed in so this stays a plain helper
// (not a hook). Falls back to the raw status text for any unknown value.
function statusLabel(t: (s: { en: string; fil: string }) => string, status: string): string {
  switch (status) {
    case 'confirmed': return t({ en: 'confirmed', fil: 'nakumpirma' });
    case 'proposed': return t({ en: 'proposed', fil: 'iminungkahi' });
    case 'needs_review': return t({ en: 'needs review', fil: 'kailangang i-review' });
    case 'excluded': return t({ en: 'excluded', fil: 'hindi kasama' });
    default: return status.replace('_', ' ');
  }
}

function rowStyle(status: string): React.CSSProperties {
  if (status === 'needs_review') return { background: 'rgba(197,16,16,0.06)' };
  if (status === 'confirmed') return { background: 'rgba(14,124,123,0.06)' };
  if (status === 'excluded') return { opacity: 0.5 };
  return {};
}
